import { SubmissionStatus } from '@bp/bp-graphql-types';
import { Grid, GridColumn, GridRow, NotificationDefaultIcon } from '@bp/ui-components';
import { BpCard } from 'components/BpCard/BpCard';
import { useSubmissions } from 'components/Submissions/hooks/useSubmissions';
import {
  SubmissionsPreviewTable,
  SubmissionsPreviewTableCallback,
} from 'components/Submissions/tables/SubmissionsPreviewTable';
import { getSubmissionStatusTranslation } from 'components/Submissions/utils/submissionUtils';
import { useActivityProtocol } from 'hooks/matrix/useActivityProtocol';
import { useConfirm } from 'hooks/useConfirm';
import { useRequireParams } from 'hooks/useRequireParams';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NotificationReason } from 'utils/matrixClient';
import { useMessageCommands } from '../../../../matrix/commands/useMessageCommands';

export const SubmissionsTab = () => {
  const { t } = useTranslation();
  const { courseUuid, assignmentUuid } = useRequireParams(['courseUuid', 'assignmentUuid']);
  const navigate = useNavigate();

  const { markAsRead } = useMessageCommands();

  const { sendMessage } = useActivityProtocol();

  const { submissions, getSubmissionPath } = useSubmissions({ courseUuid, assignmentUuid });

  const submissionsTodo = submissions.filter((s) => {
    return (
      s.status === SubmissionStatus.Todo ||
      s.status === SubmissionStatus.Draft ||
      s.status === SubmissionStatus.Feedback
    );
  });

  const submissionsNew = submissions.filter((s) => {
    return s.status === SubmissionStatus.New || s.status === SubmissionStatus.Updated;
  });

  const submissionsDone = submissions.filter((s) => s.status === SubmissionStatus.Done);

  const { ConfirmationDialog, confirm: confirmReminder } = useConfirm({
    defaultTitle: t('notifications.sendReminder'),
    defaultMessage: t('notifications.sendReminderConfirm', { count: submissionsTodo.length }),
    defaultConfirmText: t('common.send'),
  });

  function handleNavigate(submissionUuid: string) {
    navigate(getSubmissionPath(submissionUuid));
  }

  function handleSubmissionClick({ submissionUuid, newMessage, room }: SubmissionsPreviewTableCallback) {
    if (newMessage) {
      markAsRead(room?.roomId ?? '');
    }
    handleNavigate(submissionUuid);
  }

  async function handleSendReminder() {
    const confirm = await confirmReminder();
    if (!confirm) return;
    for (const submission of submissionsTodo) {
      await sendMessage(
        submission.uuid,
        NotificationReason.SubmissionComment,
        t('notifications.submissionReminder', { title: submission.assignment.title }),
      );
    }
  }

  return (
    <>
      <Grid>
        <GridRow>
          <GridColumn width={4}>
            <BpCard
              header={{
                headline: getSubmissionStatusTranslation(SubmissionStatus.Todo, 'teacher'),
                subHeadline: submissionsTodo.length,
                actions: [
                  {
                    icon: <NotificationDefaultIcon className='small' />,
                    text: t('notifications.sendReminder'),
                    callback: () => handleSendReminder(),
                    disabled: submissionsTodo.length === 0,
                  },
                ],
              }}
              noPadding
            >
              <SubmissionsPreviewTable type='todo' onRowClick={(callback) => handleSubmissionClick(callback)} />
            </BpCard>
          </GridColumn>
          <GridColumn width={4}>
            <BpCard
              header={{
                headline: getSubmissionStatusTranslation(SubmissionStatus.New, 'teacher'),
                subHeadline: submissionsNew.length,
              }}
              noPadding
            >
              <SubmissionsPreviewTable type='new' onRowClick={(callback) => handleSubmissionClick(callback)} />
            </BpCard>
          </GridColumn>
          <GridColumn width={4}>
            <BpCard
              header={{
                headline: getSubmissionStatusTranslation(SubmissionStatus.Done, 'teacher'),
                subHeadline: submissionsDone.length,
              }}
              noPadding
            >
              <SubmissionsPreviewTable type='done' onRowClick={(callback) => handleSubmissionClick(callback)} />
            </BpCard>
          </GridColumn>
        </GridRow>
      </Grid>

      <ConfirmationDialog />
    </>
  );
};
